import { Select, SelectVariant } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import type {
  IProgram,
  IProgramInteractionId,
} from '@app/domain/program/program.interface'
import { ProgramInteractionTypeId } from '@app/domain/program/program.interface'

import { AdHocInteractionSelectOptionsComponent } from './ad-hoc-interaction-select-options'
import { FollowUpInteractionSelectOptionsComponent } from './follow-up-interaction-select-options'
import { InitialInteractionSelectOptionsComponent } from './initial-interaction-select-options'
import { RemoteInteractionSelectOptionsComponent } from './remote-interaction-select-options'

const dropDownWithScroll = {
  maxHeight: '25vh',
  overflow: 'auto',
}

type Props = {
  isDisabled?: boolean
  onChange: (selected: IProgramInteractionId[]) => void
  program: IProgram
  programInteractionHistory: Record<IProgramInteractionId, Date>
  selectedInteractionIds: IProgramInteractionId[]
}

export const ProgramInteractionSelect = function ({
  isDisabled,
  onChange,
  program,
  programInteractionHistory,
  selectedInteractionIds,
}: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { isFeatureFlagEnabled } = useFeatureFlags()

  const enabledFeatureFlags = useMemo(() => {
    return Object.values(FeatureFlagId).filter((flag) =>
      isFeatureFlagEnabled(flag),
    )
  }, [isFeatureFlagEnabled])

  const selected = useMemo(() => {
    return Object.values(program.interactions)
      .flatMap((i) => i)
      .filter((i) => selectedInteractionIds?.includes(i.id))
      .map((i) => i.id)
  }, [program, selectedInteractionIds])

  const initialInteractionCompletedDate = useMemo(() => {
    if (
      program.interactions[ProgramInteractionTypeId.InitialInteraction]
        ?.length === 1
    ) {
      const initialInteraction =
        program.interactions[ProgramInteractionTypeId.InitialInteraction][0]

      return programInteractionHistory?.[initialInteraction.id]
    } else {
      return null
    }
  }, [program, programInteractionHistory])

  const extractInteractionSelectOptions = useMemo(() => {
    const options: React.ReactElement[] = []
    if (
      enabledFeatureFlags.includes(
        FeatureFlagId.InitialAndFollowUpInteraction,
      ) ||
      isDisabled
    ) {
      const initialOptions = InitialInteractionSelectOptionsComponent({
        initialInteractionCompletedDate,
        program,
        programInteractionHistory,
        isDisabled,
      })
      if (initialOptions !== null) options.push(initialOptions)

      const followUpOptions = FollowUpInteractionSelectOptionsComponent({
        initialInteractionCompletedDate,
        program,
        programInteractionHistory,
        isDisabled,
      })
      if (followUpOptions !== null) options.push(followUpOptions)
    }

    if (
      enabledFeatureFlags.includes(FeatureFlagId.AdHocInteraction) ||
      isDisabled
    ) {
      const adHocOptions = AdHocInteractionSelectOptionsComponent({
        initialInteractionCompletedDate,
        program,
        isDisabled,
      })
      if (adHocOptions !== null) options.push(adHocOptions)
    }

    if (
      enabledFeatureFlags.includes(FeatureFlagId.RemoteInteraction) ||
      isDisabled
    ) {
      const remoteOptions = RemoteInteractionSelectOptionsComponent({
        initialInteractionCompletedDate,
        program,
        programInteractionHistory,
        isDisabled,
      })
      if (remoteOptions !== null) options.push(remoteOptions)
    }
    return options
  }, [
    initialInteractionCompletedDate,
    program,
    programInteractionHistory,
    isDisabled,
    enabledFeatureFlags,
  ])

  const addSelection = useCallback(
    (selection: IProgramInteractionId) => {
      const followUpInteractionIds =
        program.interactions[ProgramInteractionTypeId.FollowUpInteraction]?.map(
          (it) => it.id,
        ) ?? []
      if (followUpInteractionIds.includes(selection)) {
        return selectedInteractionIds
          .filter((item) => !followUpInteractionIds.includes(item))
          .concat([selection])
      } else {
        return selectedInteractionIds.concat([selection])
      }
    },
    [program, selectedInteractionIds],
  )

  const onSelect = useCallback(
    (_event, selection, isPlaceholder) => {
      if (isPlaceholder) {
        onChange([])
      } else {
        let updatedSelected = selectedInteractionIds
        if (selectedInteractionIds.includes(selection)) {
          updatedSelected = selectedInteractionIds.filter(
            (item) => item !== selection,
          )
        } else {
          updatedSelected = addSelection(selection)
        }
        onChange(updatedSelected)
      }
    },
    [addSelection, onChange, selectedInteractionIds],
  )

  const onToggle = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  return (
    <Select
      variant={SelectVariant.typeaheadMulti}
      aria-label="Select Interaction"
      onToggle={onToggle}
      onSelect={onSelect}
      selections={selected}
      isOpen={isOpen}
      isDisabled={isDisabled}
      isGrouped={true}
      menuAppendTo={() => document.body}
      chipGroupProps={{ numChips: 10 }}
      style={dropDownWithScroll}
    >
      {extractInteractionSelectOptions}
    </Select>
  )
}
